import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Upload, X, FileText } from 'lucide-react';
import { Modal } from '../../../common/Modal';
import { Input } from '../../../common/Input';
import { Select } from '../../../common/Select';
import { Button } from '../../../common/Button';
import { SearchableSelect } from '../../../common/SearchableSelect';
import type { Invoice } from '../../../../types/subscriptionManagement';
import { CURRENCIES, PAYMENT_STATUSES } from '../../../../types/subscriptionManagement';
import { optionsFromLookup } from '../../../../utils/subscriptionLookupOptions';
import { generateInvoiceCoverage } from '../../../../utils/subscriptionInvoiceCoverage';

export interface InvoiceSubscriptionOption {
  id: string;
  name: string;
  currency: string;
  paymentMethod: string;
  billingCycle: string;
}

/** Same pattern as `OwnerField` in SubscriptionFormModal — search input + list. */
function SubscriptionIdSearchField({
  value,
  options,
  onChange,
}: {
  value: string;
  options: InvoiceSubscriptionOption[];
  onChange: (id: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedLabel = useMemo(() => {
    const o = options.find(s => s.id === value);
    return o ? `${o.id} — ${o.name}` : '';
  }, [options, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      s =>
        s.id.toLowerCase().includes(q) ||
        s.name.toLowerCase().includes(q),
    );
  }, [options, query]);

  return (
    <div ref={ref} className="relative">
      <Input
        label="Subscription ID *"
        fullWidth
        value={open ? query : selectedLabel}
        onChange={e => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          setOpen(true);
          setQuery('');
        }}
        placeholder={selectedLabel || 'Search by SUB-id or name…'}
        autoComplete="off"
      />
      {open && (
        <div
          className="absolute top-full mt-1 z-50 w-full bg-white border border-slate-200 rounded-lg shadow-lg py-1 overflow-auto"
          style={{ maxHeight: '200px' }}
        >
          {filtered.length > 0 ? (
            filtered.map(opt => (
              <button
                key={opt.id}
                type="button"
                onMouseDown={() => {
                  onChange(opt.id);
                  setQuery('');
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
              >
                <span className="font-mono text-slate-600">{opt.id}</span>
                <span className="text-slate-400"> — </span>
                <span>{opt.name}</span>
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-xs text-slate-400">
              {options.length === 0
                ? 'No subscriptions loaded'
                : 'No match — try a different search'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    data: Partial<Invoice>,
    opts?: { keepOpen?: boolean },
  ) => Promise<Invoice | undefined>;
  invoice: Invoice | null;
  /** All subscriptions — Subscription ID dropdown. */
  subscriptionOptions: InvoiceSubscriptionOption[];
  /** Distinct names (Lookups / subscription form). */
  paymentMethodNames: string[];
  /** Sheet Lookups tab — drives Currency / Payment status when set. */
  lookupColumns?: Record<string, string[]>;
  refreshSnapshot?: () => void;
  defaultSubscriptionId: string;
  saving: boolean;
  onUploadFile?: (invoiceId: string, file: File) => Promise<string>;
}

export const InvoiceFormModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSave,
  invoice,
  subscriptionOptions,
  paymentMethodNames,
  lookupColumns = {},
  refreshSnapshot,
  defaultSubscriptionId,
  saving,
  onUploadFile,
}) => {
  const [form, setForm] = useState<Partial<Invoice>>({});
  const [err, setErr] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedFile, setUploadedFile] = useState<{ name: string; url: string } | null>(null);

  const today = new Date().toISOString().split('T')[0];

  const paymentMethodOptions = useMemo(
    () => [
      { value: '', label: 'Select payment method…' },
      ...optionsFromLookup(
        'Payment Method',
        lookupColumns,
        paymentMethodNames,
        form.paymentMethod,
      ).map(n => ({ value: n, label: n })),
    ],
    [lookupColumns, paymentMethodNames, form.paymentMethod],
  );

  const currencyOptions = useMemo(
    () =>
      optionsFromLookup('Currency', lookupColumns, [...CURRENCIES], form.currency),
    [lookupColumns, form.currency],
  );

  const paymentStatusOptions = useMemo(
    () =>
      optionsFromLookup(
        'Payment Status',
        lookupColumns,
        [...PAYMENT_STATUSES],
        form.paymentStatus,
      ),
    [lookupColumns, form.paymentStatus],
  );

  useEffect(() => {
    if (isOpen) {
      refreshSnapshot?.();
    }
  }, [isOpen, refreshSnapshot]);

  useEffect(() => {
    if (isOpen) {
      const defSub = subscriptionOptions.find(
        s => s.id === defaultSubscriptionId,
      );
      setForm(
        invoice
          ? { ...invoice }
          : {
              subscriptionId: defaultSubscriptionId,
              invoiceDate: today,
              currency: defSub?.currency || 'USD',
              paymentMethod: defSub?.paymentMethod || '',
              paymentStatus: 'Unpaid',
            },
      );
      setErr('');
      setSelectedFile(null);
      setUploading(false);
      setUploadedFile(
        invoice?.invoiceFile ? { name: 'Attached file', url: invoice.invoiceFile } : null,
      );
    }
  }, [isOpen, invoice, defaultSubscriptionId, today, subscriptionOptions]);

  const selectedSub = useMemo(
    () => subscriptionOptions.find(s => s.id === form.subscriptionId),
    [subscriptionOptions, form.subscriptionId],
  );

  const applyCoverageFromInvoiceDate = (
    base: Partial<Invoice>,
    invoiceDate: string,
    sub?: InvoiceSubscriptionOption,
  ): Partial<Invoice> => {
    if (!sub || !invoiceDate) return base
    const generated = generateInvoiceCoverage(
      { invoiceDate },
      { billingCycle: sub.billingCycle },
    )
    if (!generated) return base
    const out = { ...base }
    if (!String(out.subscriptionStartDate || '').trim()) {
      out.subscriptionStartDate = generated.subscriptionStartDate
    }
    if (!String(out.subscriptionEndDate || '').trim()) {
      out.subscriptionEndDate = generated.subscriptionEndDate
    }
    return out
  }

  const set = (key: keyof Invoice, value: string | number) => {
    setForm(f => {
      let next: Partial<Invoice> = { ...f, [key]: value }
      if (key === 'invoiceDate' && typeof value === 'string') {
        next = applyCoverageFromInvoiceDate(next, value, selectedSub)
      }
      return next
    })
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !onUploadFile) return;
    try {
      setUploading(true);
      setErr('');
      let invId = invoice?.id || form.id;
      if (!invId || String(invId).startsWith('temp-')) {
        if (!String(form.subscriptionId || '').trim()) {
          setErr('Subscription is required before upload');
          return;
        }
        if (!form.invoiceNumber?.trim()) {
          setErr('Invoice number is required before upload');
          return;
        }
        if (!form.invoiceDate) {
          setErr('Invoice date is required before upload');
          return;
        }
        if (!form.amount || Number(form.amount) <= 0) {
          setErr('Amount is required before upload');
          return;
        }
        const saved = await onSave(form, { keepOpen: true });
        if (!saved?.id) {
          setErr('Could not create invoice for upload');
          return;
        }
        invId = saved.id;
        setForm(f => ({ ...f, ...saved }));
      }
      const url = await onUploadFile(invId, selectedFile);
      setUploadedFile({ name: selectedFile.name, url });
      setForm(f => ({ ...f, invoiceFile: url }));
      setSelectedFile(null);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!String(form.subscriptionId || '').trim()) {
      setErr('Subscription is required');
      return;
    }
    if (!form.invoiceNumber?.trim()) {
      setErr('Invoice number is required');
      return;
    }
    if (!form.invoiceDate) {
      setErr('Invoice date is required');
      return;
    }
    if (!form.amount || Number(form.amount) <= 0) {
      setErr('Amount is required');
      return;
    }
    setErr('');
    const pendingFile = selectedFile;
    const willUpload = !!(pendingFile && onUploadFile);
    try {
      if (!willUpload) {
        await onSave(form, undefined);
        return;
      }
      setUploading(true);
      const saved = await onSave(form, { keepOpen: true });
      if (!saved?.id) {
        setErr('Could not save invoice before upload');
        return;
      }
      const invId = saved.id;
      setForm(f => ({ ...f, ...saved }));
      const url = await onUploadFile(invId, pendingFile);
      setUploadedFile({ name: pendingFile.name, url });
      setSelectedFile(null);
      const finalPayload = { ...form, ...saved, invoiceFile: url };
      setForm(f => ({ ...f, ...finalPayload }));
      await onSave(finalPayload, undefined);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      if (willUpload) setUploading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={invoice || form.id ? 'Edit Invoice' : 'Add Invoice'}
      size="md"
      footer={
        <div className="flex items-center justify-between w-full">
          <div>{err && <p className="text-xs text-red-500">{err}</p>}</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="button"
              onClick={handleSubmit}
              disabled={saving || uploading}
              isLoading={saving || uploading}
            >
              {invoice ? 'Save' : 'Add'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 space-y-3">
        {(invoice?.id || form.id) && (
          <Input
            label="ID"
            fullWidth
            value={invoice?.id || form.id || ''}
            readOnly
            disabled
            className="opacity-80"
          />
        )}

        <SubscriptionIdSearchField
          value={form.subscriptionId || ''}
          options={subscriptionOptions}
          onChange={id => {
            const o = subscriptionOptions.find(s => s.id === id);
            setForm(f => {
              let next: Partial<Invoice> = {
                ...f,
                subscriptionId: id,
                currency: o?.currency || f.currency,
                paymentMethod: o?.paymentMethod || f.paymentMethod || '',
              };
              if (f.invoiceDate) {
                next = applyCoverageFromInvoiceDate(next, f.invoiceDate, o);
              }
              return next;
            });
          }}
        />

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Invoice Number *"
            fullWidth
            value={form.invoiceNumber || ''}
            onChange={e => set('invoiceNumber', e.target.value)}
            placeholder="e.g. INV-2026-042"
          />
          <Input
            label="Invoice Date *"
            fullWidth
            type="date"
            value={form.invoiceDate || ''}
            onChange={e => set('invoiceDate', e.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Subscription Start Date"
            fullWidth
            type="date"
            value={form.subscriptionStartDate || ''}
            onChange={e => set('subscriptionStartDate', e.target.value)}
          />
          <Input
            label="Subscription End Date"
            fullWidth
            type="date"
            value={form.subscriptionEndDate || ''}
            onChange={e => set('subscriptionEndDate', e.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Amount *"
            fullWidth
            type="number"
            min={0}
            step="0.01"
            value={form.amount === undefined || form.amount === null ? '' : form.amount}
            onChange={e =>
              set('amount', e.target.value === '' ? 0 : Number(e.target.value))
            }
            placeholder="0.00"
          />
          <Select
            label="Currency"
            fullWidth
            value={form.currency || 'USD'}
            onChange={e => set('currency', e.target.value)}
            options={currencyOptions.map(c => ({ value: c, label: c }))}
          />
        </div>

        <SearchableSelect
          label="Payment method"
          value={form.paymentMethod || ''}
          onChange={v => set('paymentMethod', v)}
          placeholder="Search payment method…"
          options={paymentMethodOptions}
        />

        <Select
          label="Payment status"
          fullWidth
          value={form.paymentStatus || 'Unpaid'}
          onChange={e => set('paymentStatus', e.target.value)}
          options={paymentStatusOptions.map(s => ({ value: s, label: s }))}
        />

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">
            Invoice file (PDF / image)
          </label>
          {uploadedFile ? (
            <div className="flex items-center justify-between px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex items-center gap-2 min-w-0">
                <FileText size={14} className="text-emerald-600 shrink-0" />
                <a
                  href={uploadedFile.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-emerald-700 truncate hover:underline"
                >
                  {uploadedFile.name}
                </a>
              </div>
              <button
                type="button"
                onClick={() => {
                  setUploadedFile(null);
                  setForm(f => ({ ...f, invoiceFile: '' }));
                }}
                className="text-emerald-400 hover:text-emerald-700 ml-2 shrink-0 transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <label
                htmlFor="invoice-file-input"
                className={`flex flex-col items-center justify-center w-full py-5 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                  selectedFile
                    ? 'border-blue-400 bg-blue-50'
                    : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <Upload size={20} className={`mb-1.5 ${selectedFile ? 'text-blue-500' : 'text-slate-400'}`} />
                <span className={`text-xs font-medium ${selectedFile ? 'text-blue-600' : 'text-slate-500'}`}>
                  {selectedFile ? selectedFile.name : 'Click to upload'}
                </span>
                <input
                  id="invoice-file-input"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.gif"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>
              {selectedFile && (
                <Button
                  variant="primary"
                  size="sm"
                  fullWidth
                  type="button"
                  onClick={handleUpload}
                  disabled={uploading}
                  isLoading={uploading}
                >
                  <Upload size={13} /> Upload
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
